<script setup lang="ts">
import ModelAgent from "@models/agents/ModelAgent";
import {computed } from "vue";
import {PaymentFlowRange} from "@/targets/light/types";
import UtilsService from "@/services/others/UtilsService";
import {useI18n} from "vue-i18n";
import CreatePaymentViewController from "@/targets/light/views/create-payment/ts/CreatePaymentViewController";

interface Emits {
	(e: 'selected'): void;
}

interface Props {
	agent: ModelAgent | null;
	ranges: PaymentFlowRange | null | undefined;
	currency?: 'RUB' | 'USD' | 'USDT' | 'KZT';
	loading?: boolean;
	isError?: boolean;
	selected?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	currency: 'RUB',
	loading: false,
	isError: false,
	selected: false,
});

const emits = defineEmits<Emits>();

const { t } = useI18n();

const viewController = CreatePaymentViewController.of();

const {
	isAgentBetweenRangeError,
	agentBetweenRanges,
} = viewController;

const title = computed(() => {
	if (props.agent) {
		return props.agent.name;
	}

	if (props.selected) {
		return t('light-payment.create.tabs.settings.any-agent');
	}

	return t('light-payment.create.tabs.agents-tab.title');
});

const isDisabled = computed(() => !props.ranges);

const handleSelectTab = () => {
	if (!isDisabled.value) {
		emits('selected');
	}
}


const utilsService = UtilsService.of();

const currencyFormatter = computed(() => utilsService.currencyFormatter(props.currency));

const text = computed(() => {
	if (!props.ranges?.min || !props.ranges?.max) return t('light-payment.create.tabs.settings.unavailable');

	if (isAgentBetweenRangeError.value && agentBetweenRanges.value?.length) {
		const rangesStr = agentBetweenRanges.value.map(({ min, max }) => {
				const formattedMin = currencyFormatter.value.format(min);
				const formattedMax = currencyFormatter.value.format(max);
				return `<span>${formattedMin} - ${formattedMax}</span>`;
			})
			.join('; ');

		return `${t('light-payment.create.tabs.settings.available')}: ${rangesStr}`;
	}

	if (props.ranges.min === props.ranges.max) {
		return `${t('light-payment.create.tabs.settings.available')}: <span>${currencyFormatter.value.format(props.ranges.min)}</span>`;
	}

	return `${t('light-payment.create.tabs.settings.available')}: <span>${currencyFormatter.value.format(props.ranges?.min)}</span> - <span>${currencyFormatter.value.format(props.ranges?.max)}</span>`;
});
</script>

<template>
	<f7-tab
		@click="handleSelectTab"
		:class="[
			'create-payment-agent-tab',
			{ 'tab-disabled': isDisabled },
			{ 'tab-error': isError}
		]"
		:tab-active="selected"
	>
		<i class="create-payment-agent-tab__icon i-payment-agent-svg" />
		<div class="create-payment-agent-tab__content">
			<div class="create-payment-agent-tab__title">{{ title }}</div>
			<div class="create-payment-agent-tab__ranges">
				<template v-if="!loading">
					<div v-if="isDisabled">{{ t('light-payment.create.tabs.settings.unavailable') }}</div>
					<div v-else v-html="text" />
				</template>
				<template v-else>
					<div class="custom-loader">
						<span />
					</div>
				</template>
			</div>
		</div>
		<i class="create-payment-agent-tab__arrow svg-arrow-right" />
	</f7-tab>
</template>

<style scoped lang="scss">
.create-payment-agent-tab {
	$root: #{&};
	display: flex;
	align-items: center;
	gap: .75rem;
	outline: 1px solid #DBDDE4;
	border-radius: 0.5rem;
	padding: 0.75rem 0.5rem;
	background-color: #FFFFFF;
	cursor: pointer;

	&__icon {
		flex-shrink: 0;
		width: 1.5rem;
		height: 1.5rem;
	}

	&__content {
		display: flex;
		flex-direction: column;
		gap: .125rem
	}

	&__title {
		@include text(16px, 700, var(--brown1), 120%);
	}

	&__ranges {
		@include text(12px, 500, var(--grey2), 120%);

        &:deep(div) {
            span {
                text-wrap: nowrap;
            }
        }

        span {
            text-wrap: nowrap;
        }
	}

	&__arrow {
		flex-shrink: 0;
		width: 1rem;
		height: 1rem;
		margin-left: auto;
	}

	&.tab-disabled {
		background-color: var(--grey7);
		outline-color: transparent;
		pointer-events: none;
		opacity: 0.5;
		cursor: default;
	}

	&.tab-active {
		outline-color: #007AFF;
		outline-width: 2px;
	}

	&.tab-error {
		#{$root}__ranges {
			color: var(--red);
		}
	}
}
</style>
